// src/components/bookings_manager/tour_guides/content/external_assignments/BatchActionsDrawerContent.tsx
import { useState, useMemo } from "react";
import styled from "styled-components";
import { useFetchEntities } from "@/reactQueryHooks";
import {
  useBatchAssignExternalAssignmentsToTourGroups,
  useBatchAddNoteToTourGroups,
  useBatchRemoveExternalAssignmentsFromTourGroups,
  useBatchRemoveNotesFromTourGroups,
} from "@/reactQueryHooks";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAxios } from "@/axiosProvider";
import moment from "moment";

//------------------------------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 450px;
  height: 100%;
  padding: 16px;
  background-color: #ffffff;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, sans-serif;
  font-size: 12px;
`;

const TopButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 4px;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  min-width: 400px;
  opacity: 1;
  position: relative;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  color: #4361ee;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 14px;
  }
`;

const SelectedItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const SelectedItemCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background-color: #ffffff;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 11px;
`;

const ItemDate = styled.span`
  font-weight: 600;
  color: #2b2d42;
`;

const ItemDetails = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  color: #6c757d;
`;

const NewNoteForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 4px;
  background: #f8f9fa;
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: #4361ee;
    box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.15);
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

//-----------------------------------------------------------------

interface IUser {
  _id: string;
  name: string;
  roles: IRole[];
}

interface IRole {
  _id: string;
  title: string;
  isExternalAssociate?: boolean;
}

interface IProduct {
  _id: string;
  title: string;
  options: IOption[];
  start_times: IStartTime[];
  crewRoles?: string[];
}

interface IOption {
  _id: string;
  title: string;
  crewRoles?: string[];
}

interface IStartTime {
  _id: string;
  time_slot: string;
}

interface BatchActionsDrawerContentProps {
  selectedItems: Array<{
    option_id: string;
    start_time_id: string;
    date: string;
    tour_group_id: string;
    product_id: string;
  }>;
  entities: any;
  onClose: (e: React.MouseEvent) => void;
  refetch: () => void;
  allTourGroupsData?: Array<{
    tour_group_id: string;
    external_assignments?: Array<{
      _id: string;
      role_id: string;
      user_id: string;
    }>;
    external_assignments_notes?: Array<{
      _id: string;
      user_id: string;
      body: string;
      date: Date;
    }>;
  }>;
}

const BatchActionsDrawerContent = ({
  selectedItems,
  entities: entitiesProp,
  onClose,
  refetch,
  allTourGroupsData,
}: BatchActionsDrawerContentProps) => {
  const { user } = useAxios();
  const { data: entitiesData } = useFetchEntities() as {
    data: { data: any };
  };
  const entities = entitiesData?.data || entitiesProp?.data || entitiesProp;

  const [selectedUsersWithRoles, setSelectedUsersWithRoles] = useState<
    { user: IUser; role: IRole }[]
  >([]);
  const [noteText, setNoteText] = useState("");

  const {
    mutateAsync: batchAssignExternalAssignments,
    isLoading: isAssigning,
  } = useBatchAssignExternalAssignmentsToTourGroups() as any;

  const {
    mutateAsync: batchAddNote,
    isLoading: isAddingNote,
  } = useBatchAddNoteToTourGroups() as any;

  const {
    mutateAsync: batchRemoveExternalAssignments,
    isLoading: isRemovingAssignments,
  } = useBatchRemoveExternalAssignmentsFromTourGroups() as any;

  const {
    mutateAsync: batchRemoveNotes,
    isLoading: isRemovingNotes,
  } = useBatchRemoveNotesFromTourGroups() as any;

  // Get all unique product IDs from selected items
  const uniqueProductIds = useMemo(() => {
    return Array.from(new Set(selectedItems.map((item) => item.product_id)));
  }, [selectedItems]);

  // Get all unique option IDs from selected items
  const uniqueOptionIds = useMemo(() => {
    return Array.from(new Set(selectedItems.map((item) => item.option_id)));
  }, [selectedItems]);

  // Find all eligible roles across all selected products/options
  const allEligibleRoleIds = useMemo(() => {
    const roleIds = new Set<string>();
    uniqueProductIds.forEach((productId) => {
      const product = entities?.products?.find(
        (p: IProduct) => p._id === productId
      );
      uniqueOptionIds.forEach((optionId) => {
        const option = product?.options?.find(
          (o: IOption) => o._id === optionId
        );
        if (option?.crewRoles) {
          option.crewRoles.forEach((roleId: string) => roleIds.add(roleId));
        }
      });
    });
    return Array.from(roleIds);
  }, [uniqueProductIds, uniqueOptionIds, entities]);

  // Find roles that are relevant and are marked as external associates
  const externalAssociateRoles =
    entities?.roles?.filter(
      (role: IRole) =>
        allEligibleRoleIds.includes(role._id) && role.isExternalAssociate
    ) || [];

  // Find users that have at least one of the eligible external associate roles
  const eligibleUsers =
    entities?.staff?.filter((user: IUser) => {
      const userRoleIds = user.roles.map((role) => role._id);
      return externalAssociateRoles.some((role: IRole) =>
        userRoleIds.includes(role._id)
      );
    }) || [];

  eligibleUsers?.sort((a: IUser, b: IUser) => a.name.localeCompare(b.name));

  // Prepare options for Autocomplete
  const userRoleOptions = eligibleUsers
    ?.map((user: IUser) => {
      // Find the first eligible role for this user
      const eligibleRole = user.roles.find((role) =>
        externalAssociateRoles.some((r: IRole) => r._id === role._id)
      );
      const roleData = eligibleRole
        ? entities?.roles?.find((r: IRole) => r._id === eligibleRole._id)
        : null;
      return {
        user,
        role: roleData || { _id: "", title: "Unknown" },
      };
    })
    .filter((option: { user: IUser; role: IRole }) => option.role._id !== "");

  // Get tour group IDs from selected items
  const tourGroupIds = useMemo(() => {
    return selectedItems.map((item) => item.tour_group_id);
  }, [selectedItems]);

  // Get enriched selected items for display
  const enrichedSelectedItems = useMemo(() => {
    return selectedItems.map((item) => {
      const product = entities?.products?.find(
        (p: IProduct) => p._id === item.product_id
      );
      const option = product?.options?.find(
        (o: IOption) => o._id === item.option_id
      );
      const startTime = product?.start_times?.find(
        (st: IStartTime) => st._id === item.start_time_id
      );
      // Find the tour group data to check for assignees and notes
      const tourGroupData = allTourGroupsData?.find(
        (tg) => tg.tour_group_id === item.tour_group_id
      );
      const hasAssignees =
        tourGroupData?.external_assignments &&
        tourGroupData.external_assignments.length > 0;
      const hasNotes =
        tourGroupData?.external_assignments_notes &&
        tourGroupData.external_assignments_notes.length > 0;
      return {
        ...item,
        productTitle: product?.title || "Unknown Product",
        optionTitle: option?.title || "Unknown Option",
        startTime: startTime?.time_slot || "Unknown Time",
        hasAssignees: hasAssignees || false,
        assigneesCount: tourGroupData?.external_assignments?.length || 0,
        hasNotes: hasNotes || false,
        notesCount: tourGroupData?.external_assignments_notes?.length || 0,
      };
    });
  }, [selectedItems, entities, allTourGroupsData]);

  // Get items with assignees
  const itemsWithAssignees = useMemo(() => {
    return enrichedSelectedItems.filter((item) => item.hasAssignees);
  }, [enrichedSelectedItems]);

  // Get items with notes
  const itemsWithNotes = useMemo(() => {
    return enrichedSelectedItems.filter((item) => item.hasNotes);
  }, [enrichedSelectedItems]);

  const handleBatchAssign = async () => {
    try {
      if (selectedUsersWithRoles.length === 0) {
        alert("Please select at least one user to assign");
        return;
      }

      const formattedAssignments = selectedUsersWithRoles.map((item) => ({
        role_id: item.role._id,
        user_id: item.user._id,
      }));

      const payload = {
        tour_group_ids: tourGroupIds,
        external_assignments: formattedAssignments,
      };

      await batchAssignExternalAssignments(payload);
      refetch();
      alert(`Successfully assigned ${selectedUsersWithRoles.length} user(s) to ${tourGroupIds.length} tour group(s)`);
      setSelectedUsersWithRoles([]);
    } catch (e: any) {
      console.error("Error in handleBatchAssign:", e);
      alert("Error assigning users. Please try again.");
    }
  };

  const handleBatchAddNote = async () => {
    try {
      if (!noteText.trim()) {
        alert("Please enter a note");
        return;
      }

      if (noteText.length < 3 || noteText.length > 500) {
        alert("Note must be between 3 and 500 characters.");
        return;
      }

      const payload = {
        tour_group_ids: tourGroupIds,
        note: {
          user_id: user._id,
          body: noteText,
        },
      };

      await batchAddNote(payload);
      refetch();
      alert(`Successfully added note to ${tourGroupIds.length} tour group(s)`);
      setNoteText("");
    } catch (e: any) {
      console.error("Error in handleBatchAddNote:", e);
      alert("Error adding note. Please try again.");
    }
  };

  const handleBatchRemoveAssignments = async () => {
    try {
      if (itemsWithAssignees.length === 0) {
        alert("No items with assignees to remove");
        return;
      }

      if (
        !window.confirm(
          `Are you sure you want to remove all assignments from ${itemsWithAssignees.length} tour group(s)?`
        )
      ) {
        return;
      }

      const tourGroupIdsWithAssignees = itemsWithAssignees.map(
        (item) => item.tour_group_id
      );

      const payload = {
        tour_group_ids: tourGroupIdsWithAssignees,
      };

      await batchRemoveExternalAssignments(payload);
      refetch();
      alert(
        `Successfully removed assignments from ${tourGroupIdsWithAssignees.length} tour group(s)`
      );
    } catch (e: any) {
      console.error("Error in handleBatchRemoveAssignments:", e);
      alert("Error removing assignments. Please try again.");
    }
  };

  const handleBatchRemoveNotes = async () => {
    try {
      if (itemsWithNotes.length === 0) {
        alert("No items with notes to remove");
        return;
      }

      if (
        !window.confirm(
          `Are you sure you want to remove all notes from ${itemsWithNotes.length} tour group(s)?`
        )
      ) {
        return;
      }

      const tourGroupIdsWithNotes = itemsWithNotes.map(
        (item) => item.tour_group_id
      );

      const payload = {
        tour_group_ids: tourGroupIdsWithNotes,
      };

      await batchRemoveNotes(payload);
      refetch();
      alert(
        `Successfully removed notes from ${tourGroupIdsWithNotes.length} tour group(s)`
      );
    } catch (e: any) {
      console.error("Error in handleBatchRemoveNotes:", e);
      alert("Error removing notes. Please try again.");
    }
  };

  return (
    <Container>
      <TopButtonsRow>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </TopButtonsRow>

      <ScrollableContent>
        {/* Selected Items List */}
        <SectionContainer>
          <SectionTitle>
            <i className="fas fa-list"></i>
            Selected Items ({selectedItems.length})
          </SectionTitle>
          <SelectedItemsList>
            {enrichedSelectedItems.map((item, index) => (
              <SelectedItemCard key={`${item.tour_group_id}-${index}`}>
                <ItemDate>
                  {moment(item.date).format("ddd, DD MMM YYYY")}
                </ItemDate>
                <ItemDetails>
                  <span>{item.productTitle}</span>
                  <span>•</span>
                  <span>{item.optionTitle}</span>
                  <span>•</span>
                  <span>{item.startTime}</span>
                </ItemDetails>
              </SelectedItemCard>
            ))}
          </SelectedItemsList>
        </SectionContainer>

        {/* Batch Assign Section */}
        <SectionContainer>
          <SectionTitle>
            <i className="fas fa-users"></i>
            Assign Users
          </SectionTitle>
          <Autocomplete
            multiple
            options={userRoleOptions}
            value={selectedUsersWithRoles}
            onChange={(
              _event: React.SyntheticEvent<Element, Event>,
              newValues: { user: IUser; role: IRole }[]
            ) => {
              // Remove duplicates based on user._id
              const uniqueValues = newValues.filter(
                (value, index, self) =>
                  index === self.findIndex((t) => t.user._id === value.user._id)
              );
              setSelectedUsersWithRoles(uniqueValues);
            }}
            getOptionLabel={(option) =>
              `${option.user.name} (${option.role.title})`
            }
            isOptionEqualToValue={(option, value) =>
              option.user._id === value.user._id
            }
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={`${option.user.name} (${option.role.title})`}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Select users to assign..."
              />
            )}
            size="small"
          />
          <ActionButtonsContainer>
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleBatchAssign}
              disabled={isAssigning || selectedUsersWithRoles.length === 0}
              size="small"
            >
              {isAssigning
                ? "Assigning..."
                : `Assign to ${tourGroupIds.length} tour group(s)`}
            </Button>
          </ActionButtonsContainer>
        </SectionContainer>

        {/* Batch Add Note Section */}
        <SectionContainer>
          <SectionTitle>
            <i className="fas fa-sticky-note"></i>
            Add Note
          </SectionTitle>
          <NewNoteForm>
            <NoteTextarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
            />
            <ActionButtonsContainer>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={handleBatchAddNote}
                disabled={isAddingNote || !noteText.trim()}
                size="small"
              >
                {isAddingNote
                  ? "Adding..."
                  : `Add note to ${tourGroupIds.length} tour group(s)`}
              </Button>
            </ActionButtonsContainer>
          </NewNoteForm>
        </SectionContainer>

        {/* Batch Remove Assignments Section */}
        <SectionContainer>
          <SectionTitle>
            <i className="fas fa-user-times"></i>
            Remove Assignments ({itemsWithAssignees.length} with assignees)
          </SectionTitle>
          {itemsWithAssignees.length > 0 ? (
            <>
              <SelectedItemsList>
                {itemsWithAssignees.map((item, index) => (
                  <SelectedItemCard key={`${item.tour_group_id}-${index}`}>
                    <ItemDate>
                      {moment(item.date).format("ddd, DD MMM YYYY")}
                    </ItemDate>
                    <ItemDetails>
                      <span>{item.productTitle}</span>
                      <span>•</span>
                      <span>{item.optionTitle}</span>
                      <span>•</span>
                      <span>{item.startTime}</span>
                      <span>•</span>
                      <span style={{ color: "#4361ee", fontWeight: 600 }}>
                        {item.assigneesCount} assignee(s)
                      </span>
                    </ItemDetails>
                  </SelectedItemCard>
                ))}
              </SelectedItemsList>
              <ActionButtonsContainer>
                <Button
                  startIcon={<DeleteIcon />}
                  variant="contained"
                  color="error"
                  onClick={handleBatchRemoveAssignments}
                  disabled={isRemovingAssignments}
                  size="small"
                >
                  {isRemovingAssignments
                    ? "Removing..."
                    : `Remove assignments from ${itemsWithAssignees.length} tour group(s)`}
                </Button>
              </ActionButtonsContainer>
            </>
          ) : (
            <div
              style={{
                padding: "10px",
                textAlign: "center",
                color: "#6c757d",
                backgroundColor: "rgba(67, 97, 238, 0.05)",
                borderRadius: "8px",
              }}
            >
              No selected items have assignees
            </div>
          )}
        </SectionContainer>

        {/* Batch Remove Notes Section */}
        <SectionContainer>
          <SectionTitle>
            <i className="fas fa-sticky-note"></i>
            Remove Notes ({itemsWithNotes.length} with notes)
          </SectionTitle>
          {itemsWithNotes.length > 0 ? (
            <>
              <SelectedItemsList>
                {itemsWithNotes.map((item, index) => (
                  <SelectedItemCard key={`${item.tour_group_id}-${index}`}>
                    <ItemDate>
                      {moment(item.date).format("ddd, DD MMM YYYY")}
                    </ItemDate>
                    <ItemDetails>
                      <span>{item.productTitle}</span>
                      <span>•</span>
                      <span>{item.optionTitle}</span>
                      <span>•</span>
                      <span>{item.startTime}</span>
                      <span>•</span>
                      <span style={{ color: "#4361ee", fontWeight: 600 }}>
                        {item.notesCount} note(s)
                      </span>
                    </ItemDetails>
                  </SelectedItemCard>
                ))}
              </SelectedItemsList>
              <ActionButtonsContainer>
                <Button
                  startIcon={<DeleteIcon />}
                  variant="contained"
                  color="error"
                  onClick={handleBatchRemoveNotes}
                  disabled={isRemovingNotes}
                  size="small"
                >
                  {isRemovingNotes
                    ? "Removing..."
                    : `Remove notes from ${itemsWithNotes.length} tour group(s)`}
                </Button>
              </ActionButtonsContainer>
            </>
          ) : (
            <div
              style={{
                padding: "10px",
                textAlign: "center",
                color: "#6c757d",
                backgroundColor: "rgba(67, 97, 238, 0.05)",
                borderRadius: "8px",
              }}
            >
              No selected items have notes
            </div>
          )}
        </SectionContainer>
      </ScrollableContent>
    </Container>
  );
};

export default BatchActionsDrawerContent;
